import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import useSeo from "@/lib/useSeo";
import { inr, waLink } from "@/lib/content";
import { ProductGrid } from "@/components/home/Sections";

const LOOKS = [
  { slug: "black-navratri-chaniya-choli-embroidered-dupatta", img: "/catalog/p02.webp", title: "Midnight Garba", mood: "Black chaniya choli with patch-work borders, paired with his black embroidered kurta and bright printed dhoti." },
  { slug: "white-polka-navratri-chaniya-choli", img: "/catalog/p10.webp", title: "Festive White", mood: "White polka-dot chaniya choli with a bright embroidered border, paired with his white kurta and matching Kutch-work details." },
];

const Look = ({ look, p, i }) => (
  <article data-testid={`couple-look-${i}`} className={`grid items-center gap-8 lg:grid-cols-12 lg:gap-14 ${i % 2 ? "lg:[&>div:first-child]:order-2" : ""}`}>
    <div className="lg:col-span-6">
      <div className="overflow-hidden rounded-[2.5rem] rounded-tl-[8rem] bg-[#EAE0D5]">
        <img src={look.img} alt={`Navratri couple look – ${p?.name || look.title}`} className="aspect-[4/5] w-full object-cover object-top transition-transform duration-700 hover:scale-105" />
      </div>
    </div>
    <div className="lg:col-span-6">
      <p className="eyebrow">Couple look 0{i + 1}</p>
      <h2 className="mt-3 font-serif-display text-4xl font-semibold leading-tight sm:text-5xl">{look.title}</h2>
      <p className="mt-4 text-base leading-relaxed text-[#57534E]">{look.mood}</p>
      {p && (
        <div className="mt-8 flex gap-4 rounded-3xl border border-[#E7E2DB] bg-white p-4">
          <img src={p.images[0]} alt={p.alt} className="h-28 w-20 rounded-2xl object-cover object-top" />
          <div className="flex flex-1 flex-col">
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#B85D43]">Her look</p>
            <p className="mt-1 text-sm font-medium leading-snug">{p.name}</p>
            <p className="mt-1 text-sm"><b>{inr(p.price)}</b> <span className="text-xs text-[#A8A29E] line-through">{inr(p.mrp)}</span> <span className="text-xs font-semibold text-[#1B4332]">{p.discount_pct}% off</span></p>
            <Link to={`/products/${p.slug}`} data-testid={`shop-her-look-${i}`} className="mt-auto inline-flex w-fit items-center gap-2 pt-3 text-sm font-semibold text-[#701A24]">Shop her look <ArrowRight className="h-3.5 w-3.5" /></Link>
          </div>
        </div>
      )}
      <p className="mt-4 text-xs text-[#78716C]">His kurta is shown for styling only – menswear isn't sold on Kurtaani yet.</p>
    </div>
  </article>
);

export default function NavratriEdit() {
  useSeo("Navratri Couple Looks – Matching Chaniya Choli Styles", "Plan matching Navratri couple outfits: shop Kurtaani chaniya cholis for garba and dandiya nights, with styling ideas for his look.");
  const [map, setMap] = useState({});
  const [festive, setFestive] = useState([]);
  useEffect(() => {
    LOOKS.forEach((l) => api.get(`/products/${l.slug}`).then((r) => setMap((m) => ({ ...m, [l.slug]: r.data.product }))));
    api.get("/products", { params: { category: "festive-wear", limit: 8 } }).then((r) => setFestive(r.data));
  }, []);

  return (
    <div data-testid="navratri-edit-page">
      <section className="grain relative overflow-hidden bg-[#701A24] text-[#FAF7F2]">
        <div className="kt-container grid items-center gap-10 py-16 sm:py-24 lg:grid-cols-12">
          <div className="lg:col-span-7">
          <p className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#D99B8E]"><Sparkles className="h-4 w-4" /> The Navratri Couple Edit</p>
          <h1 className="mt-5 max-w-3xl font-serif-display text-5xl font-medium leading-[1.05] sm:text-6xl lg:text-7xl">Twirl together, <em className="text-[#F2C230]">match in spirit</em></h1>
          <p className="mt-6 max-w-xl text-base text-[#EAE0D5]/85 sm:text-lg">Two garba-ready couple looks built around our chaniya cholis – pick her outfit, coordinate his, and dance all nine nights.</p>
          </div>
          <img src="/catalog/p10.webp" alt="Navratri couple in matching white festive outfits" className="hidden aspect-[4/5] w-full rounded-[2rem] rounded-tr-[6rem] object-cover object-top lg:col-span-4 lg:col-start-9 lg:block" />
        </div>
      </section>
      <div className="kt-container space-y-20 py-16 sm:space-y-28 sm:py-24">
        {LOOKS.map((l, i) => <Look key={l.slug} look={l} p={map[l.slug]} i={i} />)}
      </div>
      <section className="kt-container pb-16">
        <div className="flex flex-col items-start gap-4 rounded-3xl bg-[#1B4332] p-7 text-[#FAF7F2] sm:flex-row sm:items-center sm:justify-between sm:p-10">
          <p className="font-serif-display text-2xl sm:text-3xl">Want help matching colours for both of you?</p>
          <a href={waLink("Hi Kurtaani, I'd like help planning a Navratri couple look.")} target="_blank" rel="noreferrer" data-testid="navratri-whatsapp-button" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-semibold text-white hover:bg-[#20ba5a]"><MessageCircle className="h-4 w-4" /> Ask on WhatsApp</a>
        </div>
      </section>
      <section className="kt-container pb-20">
        <p className="eyebrow">Complete the garba wardrobe</p>
        <h2 className="mb-10 mt-3 font-serif-display text-4xl font-semibold">More Festive Styles</h2>
        <ProductGrid products={festive} testid="navratri-festive-grid" />
      </section>
    </div>
  );
}
